<script>
    import { keyScope } from "../scripts/keyboard";
    import ActionButton from "./components/ActionButton.svelte";
    import PlaygroundFeatureList from "./components/PlaygroundFeatureList.svelte";
    import PlaygroundRating from "../PlaygroundRating.svelte";
    import CopyButton from "../CopyButton.svelte";
    import PlaygroundHelper from "../scripts/playgroundhelper";

    import ApiClient from "../scripts/apiclient";
    import { createActionRunner } from "../scripts/ActionRunner";

    const { busy, run } = createActionRunner();

    export let closePopup; // Function to close the surrounding popup
    export let edit;
    $: playground = edit.playground;

    // Make sorted arrays of features to make comparison accurate
    $: playgroundFeatures = (playground?.features ?? []).toSorted();
    $: editFeatures = (edit?.features ?? []).toSorted();

    /**
     * Whether each row of the table is one the edit changes or one it leaves as it is,
     * worked out in one place so that the rows and the count of the hidden ones can never
     * disagree. The values are the css classes the rows are marked with.
     */
    $: diffs = {
        name: getDiffClass(playground.name, edit.name),
        description: getDiffClass(playground.description, edit.description),
        food: getDiffClass(playground.food, edit.food),
        toilet: getDiffClass(playground.toilet, edit.toilet),
        directions: getDiffClass(playground.directions, edit.directions),
        parking: getDiffClass(playground.parking, edit.parking),
        recommended: getDiffClass(playground.recommended, edit.recommended),
        features: getDiffClass(playgroundFeatures, editFeatures),
    };

    $: changedCount = Object.values(diffs).filter((diff) => diff === "different").length;
    $: unchangedCount = Object.values(diffs).length - changedCount;

    /**
     * Whether the rows the edit leaves alone are worth folding away. An edit that changes
     * nothing would collapse to an empty table, and one that changes everything has
     * nothing to fold, so both of those are shown whole and without a button.
     */
    $: collapsible = changedCount > 0 && unchangedCount > 0;

    /**
     * Set by the button below the table. The unchanged rows start folded away: what the
     * dialog is open to decide is what the edit changes, and the rest is there to be asked
     * for.
     */
    let showUnchanged = false;

    $: hideUnchanged = collapsible && !showUnchanged;

    /**
     * Whether the changed rows are worth marking out. The yellow answers "which of these
     * did the edit touch", which is only a question while rows of both kinds are on the
     * table.
     */
    $: markChanges = !hideUnchanged && unchangedCount > 0;

    // "fält" is the same word in both numbers, the adjective in front of it isn't
    $: unchangedText =
        unchangedCount === 1
            ? "1 oförändrat fält"
            : `${unchangedCount} oförändrade fält`;

    const reject = run(() => ApiClient.rejectEdit(edit).then(close));

    const approve = run(() => ApiClient.approveEdit(edit).then(close));

    // Closing is possible while an action is running, so that a hanging action can be
    // abandoned and retried. The flag makes sure that an action finishing after that
    // doesn't close the popup a second time, closing whatever has been opened since.
    let closed = false;

    function close() {
        if (closed || !closePopup) {
            return;
        }

        closed = true;
        closePopup();
    }

    /**
     * Archive the playground.
     */
    const archive = run(() => {
        if (!confirm("Godkänn ändringen och arkivera lekplatsen?")) {
            return;
        }

        // Approves the edit, removes the playground and closes the popup
        return ApiClient.approveEdit(edit)
            .then(() => {
                return ApiClient.setPlaygroundState(playground, "removed");
            })
            .then(close);
    });

    // This wrapper prevents the mouse event to be sent to close()
    function cancel() {
        close();
    }

    keyScope(
        {
            Enter: approve,
            Escape: cancel,
            a: reject,
        },
        { modal: true }
    );

    /**
     * Returns a css class that can be used to visualize differences in values. A different css class
     * is returned depending on whether the two specified values are equal or not.
     *
     * @param value1 The first value to compare
     * @param value2 The second value to compare
     * @returns {String} 'different' if the two values are different, 'same' if they are equal
     */
    function getDiffClass(value1, value2) {
        // Compare arrays as strings
        if (Array.isArray(value1) && Array.isArray(value2)) {
            value1 = value1.join("");
            value2 = value2.join("");
        }

        if (!value1 && !value2) {
            return "same";
        }

        return value1 === value2 ? "same" : "different";
    }
</script>

<div class="dialog">
    <h3>{PlaygroundHelper.presentPlayground(playground, false)}</h3>
    <div class="subtitles">
        <span class="subtitle"
            ><i class="fas fa-map-marker" />
            <span class="id-text">{playground._id}</span><CopyButton
                text={playground._id}
            /></span
        >
        <span class="subtitle"
            ><i class="fas fa-edit" />
            <span class="id-text">{edit._id}</span><CopyButton
                text={edit._id}
            /></span
        >
    </div>
    <PlaygroundRating {playground} />

    <div
        class="table-scroll"
        class:hide-unchanged={hideUnchanged}
        class:mark-changes={markChanges}
    >
        <table>
            <tr class="header-row">
                <th /><th>Nuvarande värde</th><th>Nytt värde</th>
            </tr>
            <tr class={diffs.name}>
                <th>Namn</th>
                <td>{playground.name || "(Tomt)"}</td>
                <td
                    ><input
                        name="name"
                        type="text"
                        bind:value={edit.name}
                    />
                </td>
            </tr>
            <tr class={diffs.description}>
                <th>Beskrivning</th>
                <td
                    ><div class="description">
                        {playground.description || "(Tomt)"}
                    </div></td
                >
                <td
                    ><textarea
                        name="description"
                        bind:value={edit.description}
                    /></td
                >
            </tr>
            <tr class={diffs.food}>
                <th>Mat</th>
                <td>{playground.food || "(Tomt)"}</td>
                <td
                    ><input
                        name="food"
                        type="text"
                        bind:value={edit.food}
                    /></td
                >
            </tr>
            <tr class={diffs.toilet}>
                <th>Toalett</th>
                <td>{playground.toilet || "(Tomt)"}</td>
                <td
                    ><input
                        name="toilet"
                        type="text"
                        bind:value={edit.toilet}
                    /></td
                >
            </tr>
            <tr class={diffs.directions}>
                <th>Ta sig hit</th>
                <td>{playground.directions || "(Tomt)"}</td>
                <td
                    ><input
                        name="directions"
                        type="text"
                        bind:value={edit.directions}
                    /></td
                >
            </tr>
            <tr class={diffs.parking}>
                <th>Parkering</th>
                <td>{playground.parking || "(Tomt)"}</td>
                <td
                    ><input
                        name="parking"
                        type="text"
                        bind:value={edit.parking}
                    /></td
                >
            </tr>
            <tr class={diffs.recommended}>
                <th>Rekommenderad</th>
                <td>{playground.recommended ? 'Ja' : 'Nej'}</td>
                <td><input type="checkbox" bind:checked={edit.recommended}></td>
            </tr>
            <tr class={diffs.features}>
                <th>Egenskaper</th>
                <td
                    ><PlaygroundFeatureList
                        features={playgroundFeatures}
                        placeholder="(Tomt)"
                    /></td
                >
                <td><PlaygroundFeatureList features={editFeatures} /></td>
            </tr>
        </table>
    </div>

    {#if collapsible}
        <button
            class="btn btn-sm btn-link unchanged-toggle"
            title={hideUnchanged
                ? "Visa även de fält ändringen låter vara"
                : "Visa bara de fält ändringen rör"}
            on:click={() => (showUnchanged = !showUnchanged)}
            >{hideUnchanged ? "Visa" : "Dölj"} {unchangedText}</button
        >
    {/if}

    <div class="button-wrapper-wrapper">
        <ActionButton
            {busy}
            action={archive}
            variant="btn-danger"
            title="Arkiverar lekplatsen så att den inte längre syns på kartan"
        >
            <span class="fa fa-trash-o" />
        </ActionButton>

        <div class="button-wrapper">
            <button class="btn btn-sm" on:click={cancel}>Avbryt</button>
            <ActionButton
                {busy}
                action={reject}
                variant="btn-warning"
                name="reject"
                title="Avslå ändringen"><u>A</u>vslå</ActionButton
            >
            <ActionButton
                {busy}
                action={approve}
                name="approve"
                title="Godkänn ändringen">Godkänn</ActionButton
            >
        </div>
    </div>
</div>

<style>
    /* No width of its own. This used to be held at 500px, which the dialog then had to be
       at least as wide as whatever the screen was. The width is the popup's to decide now,
       between the floor and ceiling it is bound with, both of which know how much screen
       there is.

       The height it does bound. Eight fields on the table make the dialog taller than the
       window on a phone, and taller again with the unchanged rows unfolded. The table
       scrolls instead, so that which playground the change is about stays at the top and
       the buttons that decide it stay at the bottom, where they can be reached. */
    .dialog {
        display: flex;
        flex-direction: column;
        height: 100%;
        /* Leaves room for the toolbar above the map, and for the tip pointing at the
           marker, so that Leaflet can fit the whole dialog inside the map. Measured
           against the dynamic viewport, so the dialog isn't cut off by the browser chrome
           of a phone. */
        max-height: calc(100vh - 160px);
        max-height: calc(100dvh - 160px);
    }

    /* Everything the table isn't keeps its height, so the table is what gives way */
    h3,
    .subtitles,
    .unchanged-toggle,
    .button-wrapper-wrapper {
        flex-shrink: 0;
    }

    h3 {
        margin-bottom: 0;
    }

    /* The two ids belong together under the name, so they share a line. They need one of
       their own: the dialog is a flex column, which would otherwise make a row of each of
       them. Wrapping is left on for the narrow case, where a phone's dialog is thinner
       than the two of them are wide. */
    .subtitles {
        display: flex;
        flex-wrap: wrap;
        column-gap: 10px;
    }

    .subtitle {
        color: silver;
        padding: 0;
        margin: 0;
    }

    input[type="text"] {
        width: 100%;
    }

    textarea {
        width: 100%;
        height: 100px;
    }

    .description {
        height: 100px;
        overflow-y: auto;
    }

    /* The archive button keeps to the left and the rest of them to the right, as they always
       have. A row rather than a float and a text alignment, so that the four of them share
       the line evenly on a phone instead of the last one hanging off the end of it. */
    .button-wrapper-wrapper {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 5px;
    }

    .button-wrapper {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-end;
        align-items: center;
        gap: 5px;
        margin-left: auto;
    }

    /* Global, since the buttons live in their own component. The gap spaces them now. */
    .button-wrapper-wrapper :global(.btn) {
        margin: 0;
    }

    table {
        width: 100%;
        margin-top: 20px;
        margin-bottom: 20px;
    }

    th {
        vertical-align: top;
        padding-top: 5px;
        padding-right: 10px;
    }

    td {
        vertical-align: top;
        width: 250px;
        padding-bottom: 10px;
        padding-top: 5px;
    }

    td:first-of-type {
        padding-right: 10px;
    }

    tr {
        border-top: 1px solid #eee;
    }

    tr:first-child {
        border-top: none;
    }

    /* The rows the edit leaves as they are, folded away until the button below the table
       asks for them. Global, since the class is worked out as the row is drawn and the
       compiler can't see it in the markup. */
    .table-scroll.hide-unchanged :global(tr.same) {
        display: none;
    }

    /* The yellow is there to tell the rows the edit changes from the rows it doesn't, which
       is only a question while both are on the table. With the rest folded away, everything
       left has changed and the table reads as the list of changes it is. */
    .table-scroll.mark-changes :global(tr.different) {
        background: #ffd;
    }

    /* Beside the table rather than among the buttons, at the end of what it counts and at
       the opposite side from the buttons that finish the dialog */
    .unchanged-toggle {
        display: block;
        padding-left: 0;
        margin: 0 0 15px 0;
    }

    /* Two columns of 250px and a label beside them want about 500px, which a narrow window
       hasn't got to give however the dialog is sized. The table keeps its shape and is
       scrolled sideways instead: the point of it is the two values next to each other, and
       stacking them one above the other would take that away. */
    .table-scroll {
        overflow-x: auto;
        overflow-y: auto;
        /* A flex item won't shrink below its content without this */
        min-height: 0;
    }

    /* A phone is narrow enough that the sideways scroll stops being a way out: the label
       leaves the two values about 120px each, and the new value, which is what the dialog
       is for, sits off the right edge until it is scrolled to. The row becomes a block of
       its own here instead, with the label above it, small and quiet, and the two values
       side by side underneath. The comparison is kept, since the values still stand next
       to each other, and the whole row is on screen at once. */
    @media (max-width: 575.98px) {
        table,
        th,
        td {
            display: block;
            width: auto;
        }

        tr {
            display: grid;
            grid-template-columns: 1fr 1fr;
            column-gap: 10px;
            padding: 5px 5px 8px 5px;
        }

        /* Above both values rather than beside them, and quiet enough to read as a label of
           the two rather than as a value of its own */
        th {
            grid-column: 1 / -1;
            padding: 0 0 3px 0;
            text-align: left;
            font-size: 11px;
            letter-spacing: 0.04em;
            text-transform: uppercase;
            color: #666;
        }

        /* The two headings have no label above them to sit under, so they take a column
           each. The cell above the labels is empty and only there to hold the corner. */
        .header-row th {
            grid-column: auto;
        }

        .header-row th:first-child {
            display: none;
        }

        td,
        td:first-of-type {
            padding: 0;
        }

        /* The row's own padding is what separates it from the next one, so the fields
           don't carry a margin below them as well */
        td input,
        td textarea {
            margin-bottom: 0;
        }

        /* No fixed height for the current description: a short one, and an empty one above
           all, would hold a hundred pixels of nothing on a screen with none to spare */
        .description {
            height: auto;
            max-height: 100px;
        }
    }
</style>
